// multiplayerQuestionService.ts - Enhanced Question Generation for Multiplayer
// Generates questions using the new multi-format architecture for multiplayer games

import { EnhancedQuestion, QuestionFormat } from '../../types/questionFormats';
import { normalizeQuestionLocale } from '../utils/questionLocales';

import { supabase } from './supabaseService';

// Multiplayer-specific question generation parameters
export interface MultiplayerQuestionParams {
  categoryId: number;
  difficultyId: number;
  questionTypes?: QuestionFormat[];
  languageCode?: string;
  totalQuestions?: number;
  formatDistribution?: Record<QuestionFormat, number>;
}

// Default format distribution for multiplayer games
const DEFAULT_MULTIPLAYER_FORMATS: Record<QuestionFormat, number> = {
  mcq_single: 0.4,      // 40% - Most familiar format
  true_false: 0.2,      // 20% - Quick answers
  mcq_multiple: 0.2,    // 20% - More challenging
  fill_blank: 0.1,      // 10% - Interactive
  ordering: 0.1         // 10% - Adds variety and engagement
};

// Enhanced format distribution for experienced players
const ENHANCED_MULTIPLAYER_FORMATS: Record<QuestionFormat, number> = {
  mcq_single: 0.3,      // 30%
  true_false: 0.2,      // 20%
  mcq_multiple: 0.2,    // 20%
  fill_blank: 0.15,     // 15%
  ordering: 0.15        // 15%
};

/**
 * Generate enhanced questions for multiplayer sessions
 */
export async function generateMultiplayerQuestions(
  params: MultiplayerQuestionParams
): Promise<EnhancedQuestion[]> {
  console.log('🔥 [MultiplayerQuestionService] FUNCTION CALLED - generateMultiplayerQuestions', params);
  
  const {
    categoryId,
    difficultyId,
    questionTypes,
    languageCode = 'en',
    totalQuestions = 10,
    formatDistribution = DEFAULT_MULTIPLAYER_FORMATS
  } = params;

  try {
    const normalizedLanguageCode = normalizeQuestionLocale(languageCode);

    console.log('🔥 [MultiplayerQuestionService] Generating questions:', {
      ...params,
      languageCode: normalizedLanguageCode,
    });

    // If specific question types are provided, use them; otherwise use all formats
    const allowedFormats = questionTypes || Object.keys(formatDistribution) as QuestionFormat[];
    
    // Calculate how many questions of each format to fetch
    const questionsPerFormat: Record<string, number> = {};
    let remainingQuestions = totalQuestions;
    
    allowedFormats.forEach((format, index) => {
      const ratio = formatDistribution[format] || 0;
      if (ratio > 0) {
        // For the last format, take all remaining questions
        if (index === allowedFormats.length - 1) {
          questionsPerFormat[format] = remainingQuestions;
        } else {
          const count = Math.floor(totalQuestions * ratio);
          questionsPerFormat[format] = count;
          remainingQuestions -= count;
        }
      }
    });

    console.log('[MultiplayerQuestionService] Questions per format:', questionsPerFormat);

    // Use the unified multi-format function (shared with Single Player)
    console.log(`[MultiplayerQuestionService] Using get_questions_for_quiz_mixed (unified with Single Player)`);
    
    const { data: dbQuestions, error: dbError } = await supabase.rpc('get_questions_for_quiz_mixed', {
      p_category_id: categoryId,
      p_difficulty_id: difficultyId,
      p_language_code: normalizedLanguageCode,
      p_limit: totalQuestions,
      p_question_types: questionTypes && questionTypes.length > 0 ? questionTypes : null,
    });

    if (dbError) {
      console.error('[MultiplayerQuestionService] Database function error:', dbError);
      throw new Error(`Database error: ${dbError.message}`);
    }

    if (!dbQuestions || dbQuestions.length === 0) {
      console.warn('[MultiplayerQuestionService] No questions returned from database function');
      console.log('[MultiplayerQuestionService] Trying supabaseService fallback...');
      
      // Use the working supabaseService fallback logic
      const { fetchQuestions } = await import('./supabaseService');
      const fallbackQuestions = await fetchQuestions({
        categoryId,
        difficultyId,
        limit: totalQuestions,
        languageCode: normalizedLanguageCode
      });
        
      if (!fallbackQuestions || fallbackQuestions.length === 0) {
        throw new Error('No questions available for this category and difficulty');
      }
      
      console.log(`[MultiplayerQuestionService] Using ${fallbackQuestions.length} questions from supabaseService fallback`);
      
      // Convert supabaseService questions to enhanced format
      const allQuestions: EnhancedQuestion[] = [];
      for (const q of fallbackQuestions) {
        try {
          const enhancedQ: EnhancedQuestion = {
            id: q.id.toString(),
            question: q.question,
            question_type: q.question_type || 'mcq_single',
            category_id: categoryId,
            difficulty_id: difficultyId,
            explanation: q.explanation,
            format_data: {
              format: q.question_type || 'mcq_single',
              options: q.options || [],
              correct_answer: q.correct_answer || q.answer || '',
              shuffle_options: true,
              min_options: 2,
              max_options: 6
            },
              // Ensure options is always an array
            options: Array.isArray(q.options) ? q.options : [],
            answer: q.answer || q.correct_answer || '',
            correct_answer: q.correct_answer || q.answer || ''
          } as any;
          
          // Only use database explanations - log if missing
          if (!enhancedQ.explanation || enhancedQ.explanation.trim() === '') {
            console.warn(`[multiplayerQuestionService] Missing explanation for ${q.question_type} question ${q.id}`);
          }
          
          allQuestions.push(enhancedQ);
        } catch (conversionError) {
          console.error('[MultiplayerQuestionService] Error converting question:', q.id, conversionError);
        }
      }
      
      return shuffleArray(allQuestions).slice(0, totalQuestions);
    }

    // Convert database function results to EnhancedQuestion format
    const allQuestions: EnhancedQuestion[] = [];
    
    for (const dbQ of dbQuestions) {
      try {
        const enhancedQ: EnhancedQuestion = {
          id: dbQ.question_id.toString(),
          question_code: dbQ.question_code || `mp-${dbQ.question_id}`,
          question: dbQ.question_text,
          question_type: dbQ.question_type,
          category_id: categoryId,
          difficulty_id: difficultyId,
          active: dbQ.active ?? true,
          explanation: dbQ.format_data?.explanation || '',
          format_data: dbQ.format_data,
          // Backward compatibility fields - will be populated below
          options: [],
          answer: '',
          correct_answer: ''
        };

        // Extract backward compatibility fields based on question type
        const formatData = dbQ.format_data as any;
        console.log(`[MultiplayerQuestionService] Converting ${dbQ.question_type} question ${dbQ.question_id}:`, {
          format_data_keys: Object.keys(formatData || {}),
          format_data: formatData
        });
        
        if (dbQ.question_type === 'mcq_single') {
          // Extract options from format_data
          // Strip trailing "?" from options — some database entries have this erroneously appended
          const cleanOpt = (s: string) => s ? s.replace(/\?$/, '').trim() : s;
          const allOptions = [formatData.option_a, formatData.option_b, formatData.option_c, formatData.option_d];
          enhancedQ.options = allOptions.filter(Boolean).map(cleanOpt);

          // Convert correct_option (a/b/c/d) to actual answer text
          const correctLetter = formatData.correct_option?.toLowerCase();
          const optionMap: { [key: string]: string } = {
            a: cleanOpt(formatData.option_a),
            b: cleanOpt(formatData.option_b),
            c: cleanOpt(formatData.option_c),
            d: cleanOpt(formatData.option_d)
          };
          enhancedQ.correct_answer = optionMap[correctLetter] || formatData.correct_option || '';
          enhancedQ.answer = enhancedQ.correct_answer;

          console.log(`[MultiplayerQuestionService] MCQ Single processed:`, {
            questionId: dbQ.question_id,
            optionsCount: enhancedQ.options.length,
            correctLetter,
            correctAnswer: enhancedQ.correct_answer,
            hasOptions: enhancedQ.options.length > 0
          });
        } else if (dbQ.question_type === 'true_false') {
          enhancedQ.options = ['True', 'False'];
          enhancedQ.correct_answer = formatData.correct_answer ? 'True' : 'False';
          enhancedQ.answer = enhancedQ.correct_answer;
        } else if (dbQ.question_type === 'mcq_multiple') {
          // Extract options from the options array and set up correct answers
          const options = formatData.options || [];
          enhancedQ.options = options.map((opt: any) => opt.text || opt.value || opt);
          
          // Find correct answers
          const correctAnswers = options.filter((opt: any) => opt.correct === true)
                                       .map((opt: any) => opt.text || opt.value || opt);
          enhancedQ.correct_answer = correctAnswers.join(', ');
          enhancedQ.answer = enhancedQ.correct_answer;
          
          // Update format_data with extracted correct answers for consistency
          enhancedQ.format_data = {
            ...formatData,
            correct_answers: correctAnswers
          };
        } else if (dbQ.question_type === 'fill_blank') {
          // For fill in blank, the template becomes the question and blanks contain the answers
          enhancedQ.question = formatData.template || dbQ.question_text;
          enhancedQ.options = []; // No options for fill in blank
          
          // Extract correct answers from blanks (actual structure: blanks[].answer)
          const blanks = formatData.blanks || [];
          const correctAnswers = blanks.map((blank: any) => blank.answer || '').filter(Boolean);
          enhancedQ.correct_answer = correctAnswers.join(', ');
          enhancedQ.answer = enhancedQ.correct_answer;
        } else if (dbQ.question_type === 'ordering') {
          // Extract items for ordering (actual structure: items[].content)
          const items = formatData.items || [];
          enhancedQ.options = items.map((item: any) => item.content || item.text || item.value || item);
          
          // The correct answer is the proper order (sorted by position field)
          const sortedItems = [...items].sort((a: any, b: any) => 
            (a.position || 0) - (b.position || 0)
          );
          enhancedQ.correct_answer = sortedItems.map((item: any) => item.content || item.text || item.value || item).join(' → ');
          enhancedQ.answer = enhancedQ.correct_answer;
        }
        
        // Only use database explanations - log if missing
        if (!enhancedQ.explanation || enhancedQ.explanation.trim() === '') {
          console.warn(`[multiplayerQuestionService] Missing explanation for ${dbQ.question_type} question ${dbQ.question_id}`);
        }
        
        allQuestions.push(enhancedQ);
        console.log(`[MultiplayerQuestionService] Processed ${dbQ.question_type} question ${dbQ.question_id}`, {
          question_type: enhancedQ.question_type,
          has_format_data: !!enhancedQ.format_data,
          options_count: enhancedQ.options?.length || 0,
          options_sample: enhancedQ.options?.slice(0, 2) || [],
          correct_answer: enhancedQ.correct_answer,
          has_explanation: !!enhancedQ.explanation,
          explanation_preview: enhancedQ.explanation ? enhancedQ.explanation.substring(0, 50) + '...' : 'NO EXPLANATION'
        });
      } catch (error) {
        console.error(`[MultiplayerQuestionService] Error converting question ${dbQ.question_id}:`, error);
      }
    }

    console.log(`[MultiplayerQuestionService] Generated ${allQuestions.length} total questions`);
    console.log('🔥 [MultiplayerQuestionService] Final questions summary:', 
      allQuestions.map(q => ({
        id: q.id,
        type: q.question_type,
        has_format_data: !!q.format_data,
        question_preview: q.question?.substring(0, 50) + '...'
      }))
    );
    
    // Apply alternating pattern for mixed formats, then slice to required count
    const alternatedQuestions = createAlternatingPattern(allQuestions);
    return alternatedQuestions.slice(0, totalQuestions);

  } catch (error) {
    console.error('[MultiplayerQuestionService] Error generating questions:', error);
    throw new Error(`Failed to generate multiplayer questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Create a multiplayer session with enhanced questions
 */
export async function createMultiplayerSessionWithQuestions(
  roomId: string,
  hostId: string,
  params: MultiplayerQuestionParams
): Promise<string> {
  console.log('🔥🔥 [MultiplayerQuestionService] createMultiplayerSessionWithQuestions CALLED', {roomId, hostId, params});
  try {
    console.log('🔥 [MultiplayerQuestionService] Creating session for room:', roomId);

    // Generate enhanced questions
    const questions = await generateMultiplayerQuestions(params);
    
    if (questions.length === 0) {
      throw new Error('No questions were generated for the multiplayer session');
    }

    // Convert questions to JSONB format for database storage
    const questionsJsonb = questions.map(q => ({
      ...q,
      // Ensure format_data is properly serialized
      format_data: typeof q.format_data === 'object' ? q.format_data : JSON.parse(q.format_data || '{}'),
      // Keep backward compatibility fields
      options: q.options || [],
      answer: q.answer || q.correct_answer || ''
    }));

    // Create the multiplayer session using the database function
    const { data: sessionId, error } = await supabase.rpc('rpc_create_multiplayer_session', {
      p_room_id: roomId,
      p_host_id: hostId,
      p_questions: questionsJsonb as any
    });

    if (error) {
      console.error('[MultiplayerQuestionService] Database error:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    if (!sessionId) {
      throw new Error('No session ID returned from database');
    }

    console.log(`[MultiplayerQuestionService] Created session ${sessionId} with ${questions.length} questions`);
    return sessionId;

  } catch (error) {
    console.error('[MultiplayerQuestionService] Error creating session:', error);
    throw error;
  }
}

/**
 * Utility function to shuffle array
 */
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Create alternating pattern between all question formats (fixed to include True/False)
 */
function createAlternatingPattern(questions: EnhancedQuestion[]): EnhancedQuestion[] {
  const mcqSingleQuestions = questions.filter(q => q.question_type === 'mcq_single');
  const mcqMultipleQuestions = questions.filter(q => q.question_type === 'mcq_multiple');
  const trueFalseQuestions = questions.filter(q => q.question_type === 'true_false');
  const fillBlankQuestions = questions.filter(q => q.question_type === 'fill_blank');
  const orderingQuestions = questions.filter(q => q.question_type === 'ordering');
  
  // Count all question types
  const questionCounts = {
    mcq_single: mcqSingleQuestions.length,
    mcq_multiple: mcqMultipleQuestions.length,
    true_false: trueFalseQuestions.length,
    fill_blank: fillBlankQuestions.length,
    ordering: orderingQuestions.length
  };
  
  console.log(`[MultiplayerQuestionService] Question counts:`, questionCounts);
  
  // If we only have one or no types, return as-is
  const activeTypes = Object.entries(questionCounts).filter(([_, count]) => count > 0);
  if (activeTypes.length <= 1) {
    console.log(`[MultiplayerQuestionService] Only ${activeTypes.length} format type(s) available, returning original order`);
    return questions;
  }
  
  // Create pools of questions by type
  const questionPools = {
    mcq_single: [...mcqSingleQuestions],
    mcq_multiple: [...mcqMultipleQuestions],
    true_false: [...trueFalseQuestions],
    fill_blank: [...fillBlankQuestions],
    ordering: [...orderingQuestions]
  };
  
  // Create pattern based on available question types
  const availableTypes = activeTypes.map(([type, _]) => type);
  const alternatedQuestions: EnhancedQuestion[] = [];
  
  // Standard pattern: cycle through all available types
  const totalQuestions = questions.length;
  let typeIndex = 0;
  
  for (let i = 0; i < totalQuestions; i++) {
    // Find next type that has questions available
    let attempts = 0;
    while (attempts < availableTypes.length) {
      const currentType = availableTypes[typeIndex] as keyof typeof questionPools;
      
      if (questionPools[currentType].length > 0) {
        // Take next question from this type
        const question = questionPools[currentType].shift()!;
        alternatedQuestions.push(question);
        break;
      }
      
      // Move to next type if current is empty
      typeIndex = (typeIndex + 1) % availableTypes.length;
      attempts++;
    }
    
    // Move to next type for variety
    typeIndex = (typeIndex + 1) % availableTypes.length;
  }
  
  console.log(`[MultiplayerQuestionService] Pattern result: [${alternatedQuestions.map(q => {
    switch(q.question_type) {
      case 'mcq_single': return 'S';
      case 'mcq_multiple': return 'M';
      case 'true_false': return 'T';
      case 'fill_blank': return 'F';
      case 'ordering': return 'O';
      default: return '?';
    }
  }).join(', ')}]`);
  
  return alternatedQuestions;
}

/**
 * Get format distribution based on difficulty and player experience
 */
export function getFormatDistribution(
  difficultyId: number,
  enhanced: boolean = false
): Record<QuestionFormat, number> {
  if (enhanced) {
    return ENHANCED_MULTIPLAYER_FORMATS;
  }

  // For easier difficulties, use more familiar formats but still include variety
  if (difficultyId === 1) { // Easy
    return {
      mcq_single: 0.5,
      true_false: 0.3,
      mcq_multiple: 0.1,
      fill_blank: 0.05,
      ordering: 0.05
    };
  }

  // Default distribution for medium/hard
  return DEFAULT_MULTIPLAYER_FORMATS;
}

/**
 * Validate that questions are properly formatted for multiplayer
 */
export function validateMultiplayerQuestions(questions: EnhancedQuestion[]): boolean {
  for (const question of questions) {
    // Check required fields
    if (!question.id || !question.question || !question.question_type) {
      console.error('[MultiplayerQuestionService] Invalid question structure:', question);
      return false;
    }

    // Check format_data exists
    if (!question.format_data) {
      console.error('[MultiplayerQuestionService] Missing format_data:', question.id);
      return false;
    }

    // Validate format-specific data
    try {
      const formatData = typeof question.format_data === 'string' 
        ? JSON.parse(question.format_data) 
        : question.format_data;

      if (formatData.format !== question.question_type) {
        console.error('[MultiplayerQuestionService] Format mismatch:', question.id);
        return false;
      }
    } catch (error) {
      console.error('[MultiplayerQuestionService] Invalid format_data JSON:', question.id, error);
      return false;
    }
  }

  return true;
}


/**
 * Enhanced question processing for Live Events
 * Extracts the same question enhancement logic used in multiplayer
 */
export function enhanceQuestionsForMultiplayer(dbQuestions: any[]): EnhancedQuestion[] {
  const allQuestions: EnhancedQuestion[] = [];
  
  for (const dbQ of dbQuestions) {
    try {
      const enhancedQ: EnhancedQuestion = {
        id: dbQ.question_id?.toString() || dbQ.id?.toString(),
        question_code: dbQ.question_code || `mp-${dbQ.question_id?.toString() || dbQ.id?.toString() || Math.random().toString(36).slice(2)}`,
        question: dbQ.question_text || dbQ.question,
        question_type: dbQ.question_type || 'mcq_single',
        category_id: dbQ.category_id || 0,
        difficulty_id: dbQ.difficulty_id || 0,
        active: dbQ.active ?? true,
        explanation: dbQ.format_data?.explanation || dbQ.explanation || '',
        format_data: dbQ.format_data,
        // Backward compatibility fields - will be populated below
        options: [],
        answer: '',
        correct_answer: ''
      };

      // Handle each format type
      const formatData = dbQ.format_data || {};
      
      if (dbQ.question_type === 'mcq_single') {
        enhancedQ.options = [formatData.option_a, formatData.option_b, formatData.option_c, formatData.option_d].filter(Boolean);
        enhancedQ.correct_answer = formatData.correct_option === 'a' ? formatData.option_a :
                                 formatData.correct_option === 'b' ? formatData.option_b :
                                 formatData.correct_option === 'c' ? formatData.option_c :
                                 formatData.correct_option === 'd' ? formatData.option_d : '';
        enhancedQ.answer = enhancedQ.correct_answer;
      } else if (dbQ.question_type === 'true_false') {
        enhancedQ.options = ['True', 'False'];
        enhancedQ.correct_answer = formatData.correct_answer ? 'True' : 'False';
        enhancedQ.answer = enhancedQ.correct_answer;
      } else if (dbQ.question_type === 'mcq_multiple') {
        const options = formatData.options || [];
        enhancedQ.options = options.map((opt: any) => opt.text || opt.value || opt);
        
        const correctAnswers = options.filter((opt: any) => opt.correct === true)
                                     .map((opt: any) => opt.text || opt.value || opt);
        enhancedQ.correct_answer = correctAnswers.join(', ');
        enhancedQ.answer = enhancedQ.correct_answer;
        
        enhancedQ.format_data = {
          ...formatData,
          correct_answers: correctAnswers
        };
      } else if (dbQ.question_type === 'fill_blank') {
        enhancedQ.question = formatData.template || dbQ.question_text || dbQ.question;
        enhancedQ.options = [];
        
        const blanks = formatData.blanks || [];
        const correctAnswers = blanks.map((blank: any) => blank.answer || '').filter(Boolean);
        enhancedQ.correct_answer = correctAnswers.join(', ');
        enhancedQ.answer = enhancedQ.correct_answer;
      } else if (dbQ.question_type === 'ordering') {
        const items = formatData.items || [];
        enhancedQ.options = items.map((item: any) => item.content || item.text || item.value || item);
        
        // The correct answer is the proper order (sorted by position field)
        const sortedItems = [...items].sort((a: any, b: any) => 
          (a.position || 0) - (b.position || 0)
        );
        enhancedQ.correct_answer = sortedItems.map((item: any) => item.content || item.text || item.value || item).join(' → ');
        enhancedQ.answer = enhancedQ.correct_answer;
      } else {
        // Fallback for unknown types
        enhancedQ.options = dbQ.options || [];
        enhancedQ.correct_answer = dbQ.correct_answer || dbQ.answer || '';
        enhancedQ.answer = enhancedQ.correct_answer;
      }
      
      allQuestions.push(enhancedQ);
    } catch (conversionError) {
      console.error('[enhanceQuestionsForMultiplayer] Error converting question:', dbQ.id || dbQ.question_id, conversionError);
    }
  }
  
  // Filter out placeholder/debug questions and deduplicate
  const filtered = allQuestions
    .filter(q => q.question && !q.question.match(/^(Multiple choice|True\/false|Fill in|Ordering) question about .+ \(ID: \d+\)/i));

  const seen = new Set<string>();
  const deduped = filtered.filter(q => {
    const key = (q.question || '').toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return deduped;
}

export { DEFAULT_MULTIPLAYER_FORMATS, ENHANCED_MULTIPLAYER_FORMATS };
